// src/components/common/ViewSwitcher.jsx
import React from 'react';
import T from './T';
import {
  Bars3BottomLeftIcon,
  ViewColumnsIcon,
  CalendarDaysIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';

const VIEW_METADATA = {
  list: {
    label: 'List',
    Icon: Bars3BottomLeftIcon,
  },
  kanban: {
    label: 'Kanban',
    Icon: ViewColumnsIcon,
  },
  calendar: {
    label: 'Calendar',
    Icon: CalendarDaysIcon,
  },
  grid: {
    label: 'Grid',
    Icon: Squares2X2Icon,
  }
};

/**
 * Standardized View Switcher Component across all BriqERP modules.
 * Unifies List, Kanban, Calendar, and Grid toggling with exact styling,
 * high-class active states, and consistent typography.
 */
export default function ViewSwitcher({
  view = 'list',
  onViewChange,
  availableViews = ['list', 'kanban'],
  showLabels = true,
  className = ''
}) {
  return (
    <div
      className={`inline-flex items-center bg-white dark:bg-[#161b22] sm:bg-gray-100 dark:sm:bg-[#21262d] p-0.5 sm:p-1 rounded-lg sm:rounded-xl border border-gray-200 dark:border-[#30363d] gap-0.5 sm:gap-1 shrink-0 h-9 sm:h-auto ${className}`}
      role="tablist"
      aria-label="View switcher"
    >
      {availableViews.map((vKey) => {
        const meta = VIEW_METADATA[vKey] || { label: vKey, Icon: Squares2X2Icon };
        const IconComponent = meta.Icon;
        const isActive = view === vKey;

        return (
          <button
            key={vKey}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onViewChange?.(vKey)}
            className={`px-2 py-1 sm:px-3 sm:py-1.5 h-7.5 sm:h-auto rounded-md sm:rounded-lg text-xs font-semibold flex items-center gap-1 sm:gap-1.5 transition-all cursor-pointer ${
              isActive
                ? 'bg-orange-50 dark:bg-[#21262d] sm:bg-white dark:sm:bg-[#161b22] text-[#E8612C] dark:text-[#E8612C] shadow-2xs border border-orange-200/60 dark:border-[#30363d]'
                : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white border border-transparent hover:bg-gray-100/50 dark:hover:bg-slate-800'
            }`}
            title={`Switch to ${meta.label} View`}
          >
            <IconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
            {showLabels && (
              <span className="hidden sm:inline">
                <T>{meta.label}</T>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
